import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { isEmpty, sortBy } from 'lodash';
import Loader from '../../features/Loader';
import { citysprintApis } from '../../apis';
import { networksActions } from '../../redux/networksSlice';
import { FORM, STRINGS } from '../../constants';
import { mapCitysprintQuoteToNetworks } from '../../helpers/mapCitysprintQuoteToNetworks';
import {
  CITYSPRINT_QUOTE_BUCKET_ORDER,
  classifyCitysprintQuoteBucket,
  formatCitysprintQuoteTimeLabel,
} from '../../helpers/citysprintQuoteBuckets';
import { Error } from '../../features/ErrorBoundary';
import { WE_HAVE_A_LITTLE_PROBLEM } from '../../features/ErrorBoundary/constants';
import { SELECT } from '../../constants/strings';
import { getValue } from '../../utils/object';
import {
  formatIndividualPrice,
  formatPriceWithVatRate,
} from '../../utils/strings';
import { CitysprintTextLogo } from '../../pages/SendCitysprintParcel/components/CitysprintTextLogo';
import { CHOOSE_FROM, NETWORK_MARKETING_DETAILS } from './constants';

const formatVehicleCapacityLine = capacity => {
  if (capacity == null || typeof capacity !== 'object') {
    return null;
  }
  const { maxLengthCM, maxWidthCM, maxHeightCM, maxWeightKG } = capacity;
  const parts = [];
  if (maxLengthCM != null && maxWidthCM != null && maxHeightCM != null) {
    parts.push(`${maxLengthCM} × ${maxWidthCM} × ${maxHeightCM} cm`);
  }
  if (maxWeightKG != null) {
    parts.push(`Up to ${maxWeightKG} kg`);
  }
  return parts.length ? parts.join(' · ') : null;
};

function citysprintQuickQuoteSortPrice(network) {
  const n = Number(network?.servicePriceWithVatRate ?? network?.servicePrice);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

function citysprintModalBannerTitle(network) {
  const bucket = classifyCitysprintQuoteBucket(network);
  const entry = CITYSPRINT_QUOTE_BUCKET_ORDER.find(o => o.key === bucket);
  return entry?.title ?? 'Same Day';
}

function pickCheapestCitysprintNetworksPerBucket(mapped) {
  if (!Array.isArray(mapped) || !mapped.length) {
    return [];
  }
  const bestByBucket = new Map();
  for (const row of mapped) {
    const bucket = classifyCitysprintQuoteBucket(row);
    const price = citysprintQuickQuoteSortPrice(row);
    const prev = bestByBucket.get(bucket);
    if (!prev || price < citysprintQuickQuoteSortPrice(prev)) {
      bestByBucket.set(bucket, row);
    }
  }
  return CITYSPRINT_QUOTE_BUCKET_ORDER.map(({ key }) => bestByBucket.get(key))
    .filter(Boolean)
    .sort(
      (a, b) =>
        citysprintQuickQuoteSortPrice(a) - citysprintQuickQuoteSortPrice(b) ||
        citysprintModalBannerTitle(a).localeCompare(
          citysprintModalBannerTitle(b)
        )
    );
}

function citysprintModalNetworkTitle(network) {
  if (
    typeof network.vehicleDescription === 'string' &&
    network.vehicleDescription.trim()
  ) {
    return network.vehicleDescription.trim();
  }
  const desc = String(network.networkDesc || '');
  const paren = desc.match(/\(([^)]+)\)\s*$/);
  if (paren?.[1]) {
    return paren[1].trim();
  }
  const stripped = desc.replace(/\s*\([^)]*\)\s*$/, '').trim();
  if (stripped) {
    return stripped;
  }
  return network.networkDesc || 'Vehicle';
}

const SameDayServicePriceNetworkRow = ({
  network,
  index,
  isLast,
  theme,
  onSubmit,
}) => {
  const config = NETWORK_MARKETING_DETAILS(theme).CITYSPRINT_SAMEDAY;
  const collect = formatCitysprintQuoteTimeLabel(network.latestCollectionTime);
  const deliver = formatCitysprintQuoteTimeLabel(network.latestDeliveryTime);
  const priceInc = formatIndividualPrice(network.servicePriceWithVatRate);
  const priceExcl = formatIndividualPrice(network.servicePrice);

  return (
    <ListItem
      sx={{
        mb: 4,
        backgroundColor: theme.vars.palette.primary.pageBackground,
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        p: 3,
        overflow: 'hidden',
        ...(isLast && { mb: 1 }),
      }}
      data-testid={`service[${index}]`}
    >
      <Typography variant='h5' sx={{ fontWeight: 600, mb: 0.5 }}>
        {citysprintModalBannerTitle(network)}
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        {`Collect by ${collect} · Deliver by ${deliver}`}
      </Typography>
      <ServiceNetworkSellingPoints config={config} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
          mt: 1,
        }}
      >
        <Typography variant='h5' sx={{ fontWeight: 600 }}>
          {`From ${priceInc}`}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {`${priceExcl} plus VAT`}
        </Typography>
      </Box>
      <Button
        variant='contained'
        color={config.buttonColor || 'primary'}
        onClick={() => onSubmit(network)}
        fullWidth
      >
        {SELECT}
      </Button>
      <Typography variant='body2' sx={{ mt: 2 }}>
        {config.deliveryTiming}
      </Typography>
    </ListItem>
  );
};

const CitysprintVehicleIcon = ({ vehicleIconUrl }) => (
  <Box
    alt=''
    component='img'
    decoding='async'
    loading='lazy'
    referrerPolicy='no-referrer'
    src={vehicleIconUrl}
    sx={{
      mb: 1,
      width: '56px',
      height: '56px',
      flexShrink: 0,
      display: 'block',
      objectFit: 'contain',
      filter:
        'brightness(0) saturate(100%) invert(18%) sepia(95%) saturate(6400%) hue-rotate(342deg) brightness(95%) contrast(92%)',
    }}
  />
);

const ServicePriceRowLeadIcon = ({ sameDayDelivery, network, config }) => {
  if (sameDayDelivery && network.vehicleIconUrl) {
    return <CitysprintVehicleIcon vehicleIconUrl={network.vehicleIconUrl} />;
  }
  const Icon = config.Icon;
  return (
    <Icon
      sx={{
        mb: 1,
        width: '56px',
        height: '56px',
        ...(config.iconSx || {}),
      }}
    />
  );
};

const ServiceNetworkSellingPoints = ({ config }) => (
  <List sx={{ p: 0 }}>
    {(config.sellingPoints || []).map(({ Icon: PointIcon, text }, i) => (
      <ListItem
        key={i}
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          pl: 0,
        }}
      >
        <ListItemIcon sx={{ minWidth: '52px' }}>
          <PointIcon
            sx={{
              width: '40px',
              height: '40px',
              ...(config.iconSx || {}),
            }}
          />
        </ListItemIcon>
        <ListItemText>{text}</ListItemText>
      </ListItem>
    ))}
  </List>
);

const NetworkRowPrice = ({ network, isInternational }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
    <Typography variant='h5' sx={{ fontWeight: 600 }}>
      {isInternational
        ? `£${Number(network.servicePrice || 0).toFixed(2)}`
        : formatPriceWithVatRate(
            network.servicePriceWithVatRate,
            network.servicePrice
          )}
    </Typography>
  </Box>
);

const ServicePriceNetworkRow = ({
  network,
  index,
  isLast,
  theme,
  sameDayDelivery,
  isInternational,
  onSubmit,
}) => {
  const config =
    NETWORK_MARKETING_DETAILS(theme)[network.networkCode] ||
    NETWORK_MARKETING_DETAILS(theme).CITYSPRINT_SAMEDAY;
  const vehicleCapacityLine = formatVehicleCapacityLine(
    network.vehicleCapacity
  );
  const bannerTextColor =
    config.bannerTextColor ?? theme.vars.palette.primary.contrastText;
  const ribbonLabel = sameDayDelivery
    ? citysprintModalBannerTitle(network)
    : config.bannerText;
  const titleLabel = sameDayDelivery
    ? citysprintModalNetworkTitle(network)
    : network.networkDesc;

  return (
    <ListItem
      sx={{
        mb: 4,
        backgroundColor: theme.vars.palette.primary.pageBackground,
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        p: 3,
        overflow: 'hidden',
        ...(isLast && { mb: 1 }),
      }}
      data-testid={`service[${index}]`}
    >
      <Box
        sx={{
          backgroundColor: config.bannerColor,
          position: 'absolute',
          top: 0,
          right: 0,
          transform: 'translate(22%, 110%) rotate(45deg)',
          p: 1,
          width: '200px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant='body2'
          sx={{ fontWeight: 600, color: bannerTextColor }}
        >
          {ribbonLabel}
        </Typography>
      </Box>
      <ServicePriceRowLeadIcon
        sameDayDelivery={sameDayDelivery}
        network={network}
        config={config}
      />
      <Typography
        variant='h5'
        sx={{
          fontWeight: 600,
          mb: 1,
          maxWidth: { xs: '185px', sm: '230px' },
        }}
      >
        {titleLabel}
      </Typography>
      {sameDayDelivery && vehicleCapacityLine && (
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ mb: 1, maxWidth: '280px' }}
        >
          {vehicleCapacityLine}
        </Typography>
      )}
      <Typography variant='body2' sx={{ mb: 1, maxWidth: '250px' }}>
        {config.description}
      </Typography>
      <ServiceNetworkSellingPoints config={config} />
      <NetworkRowPrice isInternational={isInternational} network={network} />
      <Button
        variant='contained'
        color={config.buttonColor || 'primary'}
        onClick={() => onSubmit(network)}
        fullWidth
      >
        {SELECT}
      </Button>
      <Typography variant='body2' sx={{ mt: 2 }}>
        {config.deliveryTiming}
      </Typography>
    </ListItem>
  );
};

const ServicePricesModal = ({
  open,
  onClose,
  onSubmit,
  values = {},
  isInternational,
  sameDayDelivery = false,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [isError, setIsError] = useState(false);

  const loadNetworkPrices = useCallback(async () => {
    if (sameDayDelivery) {
      const trimPostcode = key =>
        String(getValue(values, key, '') ?? '').trim();

      const deliveryPostcode = trimPostcode(
        FORM.SHIPMENT_FIELDS.DESTINATION_POSTCODE.KEY
      );
      const collectionPostcode = trimPostcode(
        FORM.SHIPMENT_FIELDS.COLLECTION_POSTCODE.KEY
      );

      if (!deliveryPostcode || !collectionPostcode) {
        setIsError(true);
        throw new Error(STRINGS.SAME_DAY_POSTCODES_REQUIRED);
      }

      const response = await citysprintApis.getQuotes({
        collectionPostcode,
        deliveryPostcode,
      });

      const mapped = mapCitysprintQuoteToNetworks(response?.data);

      if (!mapped?.length) {
        setIsError(true);
        throw new Error(
          STRINGS.NO_SAME_DAY_SERVICES_AVAILABLE_FOR_THE_GIVEN_POSTCODES
        );
      }

      const filtered = pickCheapestCitysprintNetworksPerBucket(mapped);
      if (!filtered.length) {
        setIsError(true);
        throw new Error(
          STRINGS.NO_SAME_DAY_SERVICES_AVAILABLE_FOR_THE_GIVEN_POSTCODES
        );
      }

      return filtered;
    }

    const postcode = getValue(
      values,
      FORM.SHIPMENT_FIELDS.DESTINATION_POSTCODE.KEY,
      ''
    );
    const countryCode = getValue(
      values,
      FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY,
      STRINGS.GB
    );

    const quickQuoteNetworks = await dispatch(
      networksActions.fetchServicePrices({
        weight: values.weight || 0,
        countryCode,
        ...(!isEmpty(postcode) ? { postcode } : {}),
      })
    ).unwrap();

    if (!quickQuoteNetworks?.length) {
      setIsError(true);

      throw new Error(STRINGS.NO_NETWORKS_ERROR);
    }

    return sortBy(quickQuoteNetworks, ['servicePrice']);
  }, [dispatch, values, sameDayDelivery]);

  useEffect(() => {
    if (open) {
      setIsError(false);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      aria-labelledby='service-prices-modal-title'
      data-testid='service-prices-modal'
    >
      <Box
        sx={{
          position: 'absolute',
          overflow: 'hidden',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: { xs: 2, md: 4 },
          borderRadius: 2,
          maxWidth: '90vw',
          maxHeight: '90vh',
          minHeight: 'fit-content',
        }}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={2}
        >
          <Typography
            id='service-prices-modal-title'
            variant='h2'
            component='h2'
            color='primary.main'
          >
            {!isError && CHOOSE_FROM}
          </Typography>
          <IconButton onClick={onClose} data-testid='close-modal'>
            <CloseIcon />
          </IconButton>
        </Box>
        {sameDayDelivery && <CitysprintTextLogo />}
        <Loader
          promiseFn={loadNetworkPrices}
          errorComponent={() => {
            setIsError(true);

            return (
              <Error
                title={WE_HAVE_A_LITTLE_PROBLEM}
                descriptionArray={[
                  sameDayDelivery
                    ? STRINGS.NO_SAME_DAY_SERVICES_AVAILABLE_FOR_THE_GIVEN_POSTCODES
                    : STRINGS.NO_NETWORKS_ERROR,
                ]}
              />
            );
          }}
        >
          {(networks = []) => (
            <List sx={{ overflowY: 'auto', maxHeight: 'calc(90vh - 88px)' }}>
              {networks.map((network, index) =>
                sameDayDelivery ? (
                  <SameDayServicePriceNetworkRow
                    key={`${network.networkCode}-${index}`}
                    index={index}
                    isLast={index === networks.length - 1}
                    network={network}
                    onSubmit={onSubmit}
                    theme={theme}
                  />
                ) : (
                  <ServicePriceNetworkRow
                    key={`${network.networkCode}-${index}`}
                    index={index}
                    isInternational={isInternational}
                    isLast={index === networks.length - 1}
                    network={network}
                    onSubmit={onSubmit}
                    sameDayDelivery={sameDayDelivery}
                    theme={theme}
                  />
                )
              )}
            </List>
          )}
        </Loader>
      </Box>
    </Modal>
  );
};

ServicePricesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  isInternational: PropTypes.bool,
  sameDayDelivery: PropTypes.bool,
};

export default ServicePricesModal;
